import React, { useEffect, useRef, useState, useCallback, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { useVideoState } from './hooks/useVideoState';
import { useKeyboard } from './hooks/useKeyboard';
import VideoCanvas from './VideoCanvas';
import ControlBar from './ControlBar';
import CloseButton from './CloseButton';
import posthog from '@/lib/posthog';
import { getVideoTrackingEvent } from '@/lib/videoTracking';
import { VIDEO_CONFIG } from '@/lib/videoConfig';
import { shouldShowCompletionOverlay } from './completionTiming';

const FilmCompletionOverlay = lazy(() => import('./FilmCompletionOverlay'));

interface CinematicPortalProps {
  isOpen: boolean;
  onClose: () => void;
  videoSrc: string;
  posterSrc?: string;
  title?: string;
  aspectRatio?: '16:9' | '9:16' | 'auto';
  onFilmComplete?: () => void;
  // Tracking context
  sourcePage?: string;
  sourceAction?: string;
  // CTA behavior
  showCTAOnEnd?: boolean;
}

const CinematicPortal: React.FC<CinematicPortalProps> = ({
  isOpen,
  onClose,
  videoSrc,
  posterSrc,
  title = 'Watch The Fawn Friends Prologue',
  aspectRatio = 'auto',
  onFilmComplete,
  sourcePage,
  sourceAction,
  showCTAOnEnd = false
}) => {
  const portalRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const scrollYRef = useRef(0);

  const [isAnimating, setIsAnimating] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [completionDismissed, setCompletionDismissed] = useState(false);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Video tracking state
  const [hasStartedTracking, setHasStartedTracking] = useState(false);
  const [trackedMilestones, setTrackedMilestones] = useState<Set<number>>(new Set());

  const {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isFullscreen,
    buffered,
    hasEnded,
    play,
    pause,
    seek,
    setVolume,
    toggleMute,
    toggleFullscreen
  } = useVideoState(videoRef, isOpen);

  // Handle keyboard shortcuts
  useKeyboard({
    isOpen,
    onClose,
    play,
    pause,
    isPlaying,
    seek,
    currentTime,
    duration,
    setVolume,
    volume,
    toggleMute,
    toggleFullscreen
  });


  const lockScroll = useCallback(() => {
    scrollYRef.current = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }, []);

  const unlockScroll = useCallback(() => {
    const y = scrollYRef.current;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, y);
  }, []);

  // Handle portal mount/unmount with animations
  useEffect(() => {
    if (isOpen && !isMounted) {
      setIsMounted(true);
      setIsAnimating(true);
      lockScroll();

      // Simplified animation - use setTimeout to ensure refs are ready
      setTimeout(() => {
        if (!backdropRef.current || !playerRef.current) {
          setIsAnimating(false);
          return;
        }

        const tl = gsap.timeline({
          onComplete: () => setIsAnimating(false)
        });

        tl.set([backdropRef.current, playerRef.current], {
          visibility: 'visible'
        })
        .fromTo(backdropRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.3, ease: 'power2.out' }
        )
        .fromTo(playerRef.current,
          { scale: 0.9, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.2)' },
          '-=0.1'
        );

        timelineRef.current = tl;
      }, 50);
    } else if (!isOpen && isMounted) {
      // Exit animation
      setIsAnimating(true);

      const tl = gsap.timeline({
        onComplete: () => {
          setIsMounted(false);
          setIsAnimating(false);
          unlockScroll();
        }
      });

      tl.to(playerRef.current, {
        scale: 0.95,
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in'
      })
      .to(backdropRef.current, {
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in'
      }, '-=0.1');

      timelineRef.current = tl;
    }

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, [isOpen, isMounted, lockScroll, unlockScroll]);

  // Safety net: restore scroll if component unmounts unexpectedly
  useEffect(() => {
    return () => unlockScroll();
  }, [unlockScroll]);

  useEffect(() => {
    if (!isOpen) setCompletionDismissed(false);
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isAnimating) {
      onClose();
    }
  }, [onClose, isAnimating]);

  // Handle play/pause toggle
  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  // Handle mouse movement to show/hide controls
  const handleMouseMove = useCallback(() => {
    setShowControls(true);

    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    // Hide controls after 3 seconds if playing
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  // Show controls when paused, hide when playing
  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    } else {
      // Start the hide timer when playing starts
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying]);

  // Video tracking effects
  useEffect(() => {
    // Track video start - only for our two main films
    if (isPlaying && currentTime > 0 && !hasStartedTracking) {
      const trackingConfig = getVideoTrackingEvent(videoSrc);

      if (trackingConfig) {
        posthog.capture(trackingConfig.eventName, {
          video_src: videoSrc,
          video_title: title,
          duration: duration,
          source_page: sourcePage || 'unknown',
          source_action: sourceAction || 'unknown'
        });
      }
      // Don't track other videos to reduce noise
      setHasStartedTracking(true);
    }
  }, [isPlaying, currentTime, hasStartedTracking, videoSrc, title, duration, sourcePage, sourceAction]);

  useEffect(() => {
    // Track video progress milestones - only for our two main films
    const trackingConfig = getVideoTrackingEvent(videoSrc);
    if (trackingConfig && duration > 0 && currentTime > 0) {
      const progressPercent = (currentTime / duration) * 100;
      const milestones = [25, 50, 75, 95];

      milestones.forEach(milestone => {
        if (progressPercent >= milestone && !trackedMilestones.has(milestone)) {
          posthog.capture('video_progress', {
            video_src: videoSrc,
            video_title: title,
            video_type: trackingConfig.clickEventName.replace('watch_', ''), // 'animated_film' or 'product_film'
            progress_percent: milestone,
            current_time: currentTime,
            duration: duration,
            milestone: `${milestone}_percent`,
            source_page: sourcePage || 'unknown',
            source_action: sourceAction || 'unknown'
          });
          setTrackedMilestones(prev => new Set([...prev, milestone]));
        }
      });
    }
  }, [currentTime, duration, trackedMilestones, videoSrc, title, sourcePage, sourceAction]);

  // Optimize video performance when completion overlay shows
  useEffect(() => {
    if (hasEnded && videoRef.current) {
      const video = videoRef.current;

      // Pause the video to stop any background processing
      video.pause();

      // Reduce video quality/remove poster to free up memory
      video.removeAttribute('poster');

      // Optional: You could also set video.src = '' to completely unload,
      // but this might cause issues if user wants to replay
    }
  }, [hasEnded]);

  // Optimize video playback to prevent glitches
  useEffect(() => {
    if (videoRef.current && isOpen) {
      const video = videoRef.current;

      // Ensure hardware acceleration
      video.style.transform = 'translateZ(0)';

      // Prevent layout thrashing during playback
      const preventLayoutThrashing = () => {
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA
          video.style.willChange = 'auto';
        }
      };

      video.addEventListener('canplay', preventLayoutThrashing);

      return () => {
        video.removeEventListener('canplay', preventLayoutThrashing);
      };
    }
  }, [isOpen]);

  if (!isMounted) return null;

  // Determine if completion overlay should show
  const showCompletionOverlay = shouldShowCompletionOverlay({
    currentTime,
    duration,
    hasEnded,
    dismissed: completionDismissed,
    showCTAOnEnd,
    cueTimeSeconds: videoSrc === VIDEO_CONFIG.MAIN_ANIMATED_FILM.url
      ? VIDEO_CONFIG.MAIN_ANIMATED_FILM.completionCueSeconds
      : undefined
  });

  return createPortal(
    <div
      ref={portalRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/95"
        style={{ opacity: 0, visibility: 'hidden' }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Player Container */}
      <div
        ref={playerRef}
        className="relative w-full max-w-[90vw] md:max-w-[80vw] lg:max-w-[1280px] mx-4"
        style={{ maxHeight: '90vh', opacity: 0, visibility: 'hidden' }}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseMove}
      >
        {/* Close Button */}
        <div
          className={`transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
          style={{ pointerEvents: showControls ? 'auto' : 'none' }}
        >
          <CloseButton onClick={onClose} />
        </div>

        {/* Video Canvas */}
        <div
          className={`transition-opacity duration-500 ${showCompletionOverlay ? 'opacity-0' : 'opacity-100'}`}
          style={{ pointerEvents: showCompletionOverlay ? 'none' : 'auto' }}
        >
          <VideoCanvas
            ref={videoRef}
            src={videoSrc}
            poster={posterSrc}
            isPlaying={isPlaying}
            onTogglePlay={togglePlay}
            aspectRatio={aspectRatio}
            showControls={showControls}
          />
        </div>

        {/* Control Bar */}
        <div
          className={`transition-opacity duration-300 ${(showControls && !showCompletionOverlay) ? 'opacity-100' : 'opacity-0'}`}
          style={{ pointerEvents: (showControls && !showCompletionOverlay) ? 'auto' : 'none' }}
        >
          <ControlBar
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            volume={volume}
            isMuted={isMuted}
            isFullscreen={isFullscreen}
            buffered={buffered}
            onTogglePlay={togglePlay}
            onSeek={seek}
            onVolumeChange={setVolume}
            onToggleMute={toggleMute}
            onToggleFullscreen={toggleFullscreen}
          />
        </div>

        {/* Film Completion Overlay */}
        <Suspense fallback={null}>
          <FilmCompletionOverlay
            isVisible={showCompletionOverlay}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            videoSrc={videoSrc}
            videoTitle={title}
            onDismiss={() => setCompletionDismissed(true)}
          />
        </Suspense>
      </div>
    </div>,
    document.body
  );
};

export default CinematicPortal;
